/**
 * Services layer: UI-free game services (R2 extracts this folder to packages/services).
 * Everything platform-specific (storage, fetch, level loaders, audio module, ads/IAP/identity
 * providers) is injected; the providers come from @mp/adapters' createProviders.
 */
import { apiPlatform, type Platform } from '@mp/adapters/platform';
import { createProviders, type ProviderSelection, type Providers, type ProvidersConfig } from '@mp/adapters/providers';
import { remoteDefaults, type RemoteConfig } from '../config/content.js';
import { AccountService } from './account.js';
import { AdsService } from './ads.js';
import { Analytics } from './analytics.js';
import { ApiClient } from './api.js';
import { AudioAdapter, type AudioEngine } from './audio.js';
import { DailyService } from './daily.js';
import { LevelRepo, type LevelSources } from './levels.js';
import { LEVEL_OUTDATED, OutdatedLevels, currentLevelOf } from './outdated.js';
import { Privacy } from './privacy.js';
import { restoreRemote } from './remoteConfig.js';
import { PlayerStore } from './store.js';
import type { KV } from './storage.js';
import { SyncService, type SyncHooks } from './sync.js';
import type { LevelV1 } from '@mp/core';
import type { Realtime } from './realtime.js';

export interface ServiceDeps {
  kv: KV;
  fetch: typeof fetch;
  apiUrl: string;
  now: () => number;
  levelSources: LevelSources;
  audioLoader: (() => Promise<{ createAudio?: () => AudioEngine }>) | null;
  /** MP_PROVIDERS-style selection (missing families default to dev) */
  providers?: string | Partial<ProviderSelection>;
  /** location.search, for dev knobs (?ads=nofill…) */
  search?: string;
  /** dev ads countdown in seconds */
  adSeconds?: number;
  release?: string;
  /** privacy region inputs (CDN country / ?country= in dev) */
  country?: string | null;
  timeZone?: string | null;
  /** offline daily puzzle generator (lazy chunk) */
  generateDaily: (date: string) => Promise<{ level: LevelV1; seed: number }>;
  syncHooks?: SyncHooks;
  /** apps/realtime WebSocket URL (VITE_REALTIME_URL; default ws://localhost:8788/ws) */
  realtimeUrl?: string;
  /** where the game runs (detectPlatform() in main.ts); createProviders detects it when unset */
  platform?: Platform;
  /**
   * What only a platform build knows: the native entry chunk's plugin loaders (literal
   * imports), link config, AdMob units, Play base plans and sign-in sheets (bootNative().providers),
   * or the Discord Activity's sign-in (bootDiscord().providers). Null on the plain web.
   */
  platformProviders?: PlatformProviders | null;
}

/** createProviders settings a platform build adds (src/native, src/discord). */
export type PlatformProviders = Pick<ProvidersConfig, 'native' | 'links' | 'ads' | 'iap' | 'auth'>;

export interface Services {
  kv: KV;
  store: PlayerStore;
  api: ApiClient;
  analytics: Analytics;
  sync: SyncService;
  levels: LevelRepo;
  audio: AudioAdapter;
  providers: Providers;
  ads: AdsService;
  privacy: Privacy;
  daily: DailyService;
  account: AccountService;
  /** level_outdated answers: refresh + resend, and the once-per-session update message */
  outdated: OutdatedLevels;
  /** remote-config defaults merged with /v1/config when reachable */
  remote: RemoteConfig;
  /** the realtime socket (duels, war boards, presence): created on first use, lazy chunk */
  realtime: () => Promise<Realtime>;
  now: () => number;
}

export function createServices(d: ServiceDeps): Services {
  const store = PlayerStore.load(d.kv, d.now());
  const holder: { providers: Providers | null; outdated: OutdatedLevels | null } = { providers: null, outdated: null };
  const api = new ApiClient({
    baseUrl: d.apiUrl.replace(/\/$/, ''),
    fetch: d.fetch,
    deviceId: () => store.state.deviceId,
    locale: () => store.state.settings.locale,
    platform: () => (holder.providers ? apiPlatform(holder.providers.platform) : 'web'),
    getAuth: () => store.state.auth,
    setAuth: (a) => store.update((s) => (s.auth = a)),
    attribution: () => store.state.attribution,
    installReferrer: () => store.state.installReferrer,
    installReferrerSent: () => store.update((s) => (s.installReferrer = null)),
    birthYear: () => store.state.birthYear,
    now: d.now,
    onLevelOutdated: (body) => {
      const current = currentLevelOf(body);
      holder.outdated?.report(current?.levelId ?? null, current);
    },
  });
  const remote: RemoteConfig = { ...remoteDefaults, flags: { ...remoteDefaults.flags } };
  restoreRemote(d.kv, store.state.auth?.playerId ?? null, remote); // this player's last answer, until the next GET /v1/config
  const hooks: SyncHooks = { ...d.syncHooks };
  const sync = new SyncService(store, api, d.now, {
    onRejected: (op, reason) => {
      // not a cheat or a loss: refresh + resend once, or the update message (services/outdated.ts)
      if (reason === LEVEL_OUTDATED) void outdated.rejected(op);
      hooks.onRejected?.(op, reason);
    },
    onResult: (op, r) => hooks.onResult?.(op, r),
    onStatus: (st) => hooks.onStatus?.(st),
    onReconciled: (snap) => hooks.onReconciled?.(snap),
  });
  const audio = new AudioAdapter(d.audioLoader);
  audio.configure(store.state.settings.sound, store.state.settings.music);
  const extra = d.platformProviders ?? {};
  const h5 = extra.ads?.h5;
  const providers = createProviders({
    ...extra,
    providers: d.providers ?? '',
    api,
    kv: d.kv,
    ...(d.platform ? { platform: d.platform } : {}),
    search: d.search ?? '',
    ads: {
      ...extra.ads,
      dev: { seconds: d.adSeconds ?? 5 },
      // H5 Games Ads policy: game sound off from beforeAd to afterAd (overlaps the AdsService hold; the adapter counts)
      ...(h5 ? { h5: { ...h5, onPause: () => audio.pauseForAd(), onResume: () => audio.resumeAfterAd() } } : {}),
    },
    // AdMob SSV user_id, StoreKit appAccountToken / Play obfuscatedAccountId: the signed-in player
    playerId: () => store.state.auth?.playerId ?? null,
    purchaseGuard: () => ({ coppa: store.state.coppa, parentVerified: store.state.parentVerified }),
    crash: { release: d.release ?? 'dev' },
    hapticsEnabled: () => store.state.settings.vibration,
  });
  holder.providers = providers;
  const privacy = new Privacy({ store, consent: providers.consent, api, sync, now: d.now, country: d.country ?? null, ...(d.timeZone !== undefined ? { timeZone: d.timeZone } : {}) });
  const analytics = new Analytics({
    kv: d.kv,
    send: api.enabled ? (ev) => api.analytics(ev) : null,
    now: d.now,
    allowed: () => privacy.trackingAllowed(),
  });
  const ads = new AdsService({ api, provider: providers.ads, kv: d.kv, store, sync, analytics, now: d.now, flags: () => remote.flags, interstitialRules: () => remote.interstitial, audio });
  const daily = new DailyService({ api, store, sync, now: d.now, generate: d.generateDaily });
  const account = new AccountService({ api, store, sync, email: providers.email, identity: providers.identity, now: d.now });
  const levels = new LevelRepo(d.levelSources);
  const outdated = new OutdatedLevels({ levels, store, sync: () => sync });
  holder.outdated = outdated;
  // wire the late hooks (callers may set them after creation)
  Object.defineProperty(sync, 'hooksRef', { value: hooks });
  let rt: Promise<Realtime> | null = null;
  const realtime = () =>
    (rt ??= import('./realtime.js').then(
      ({ Realtime }) => new Realtime({ url: d.realtimeUrl ?? 'ws://localhost:8788/ws', token: (fresh) => api.accessToken(fresh), now: d.now }),
    ));
  return { kv: d.kv, store, api, analytics, sync, levels, audio, providers, ads, privacy, daily, account, outdated, remote, realtime, now: d.now };
}

/** Replace sync hooks after creation (the UI exists only later). */
export function setSyncHooks(s: Services, h: SyncHooks): void {
  const ref = (s.sync as unknown as { hooksRef: SyncHooks }).hooksRef;
  Object.assign(ref, h);
}

export type { PlayerState } from './state.js';
